import { getInitialInflationLabel, parseInflationRate } from '@/utils/inflationParser';
import type { Quote } from '@/model';
import { createQuoteWithROIandProjectedCoverage } from '@/util/policyQuoteCalculations';
import { adjustQuoteForPremium } from '@/util/quoteAdjustment';
import {
  benefitPeriodToString,
  benefitPeriodsMatch,
  getBenefitPeriodFromQuote,
  stringToBenefitPeriod,
  type BenefitPeriod,
} from '@/utils/benefitPeriod';
import { calculateInvestmentBounds } from '@/utils/investmentBounds';
import { getPaymentOptionFromQuote, type PaymentOption } from '@/utils/quotePaymentOption';
import { formatROI } from '@shared';
import { useEffect, useMemo, useState } from 'react';

interface UseQuoteCustomizationProps {
  allCarrierQuotes: Quote[];
  topQuote: Quote;
  client: Client | undefined;
  returnOnInvestmentCalculationType?: ReturnOnInvestmentCalculationType;
}

interface AvailableOptions {
  benefitPeriods: BenefitPeriod[];
  inflationOptions: Array<{ label: string; rate: number }>;
  paymentTypes: PaymentOption[];
}

interface QuoteFilterParams {
  quote: Quote;
  selectedBenefitPeriod: BenefitPeriod;
  selectedInflationOption: string;
  selectedPaymentType: PaymentOption;
}

function matchesQuoteFilters({
  quote,
  selectedBenefitPeriod,
  selectedInflationOption,
  selectedPaymentType,
}: QuoteFilterParams): boolean {
  const quotePeriod = getBenefitPeriodFromQuote(quote);
  const periodMatches = benefitPeriodsMatch(quotePeriod, selectedBenefitPeriod);

  let inflationMatches = false;
  if (selectedInflationOption === 'None') {
    inflationMatches =
      (!quote.inflationBenefitOption || quote.inflationBenefitOption === 'None') &&
      (!quote.aobInflation || quote.aobInflation === 'None') &&
      (!quote.cobInflation || quote.cobInflation === 'None');
  } else {
    inflationMatches =
      quote.inflationBenefitOption === selectedInflationOption ||
      quote.aobInflation === selectedInflationOption ||
      quote.cobInflation === selectedInflationOption;
  }

  const paymentTypeMatches = getPaymentOptionFromQuote(quote) === selectedPaymentType;

  return periodMatches && inflationMatches && paymentTypeMatches;
}

export function useQuoteCustomization({
  allCarrierQuotes,
  topQuote,
  client,
  returnOnInvestmentCalculationType = 'total',
}: UseQuoteCustomizationProps) {
  const [isCustomizing, setIsCustomizing] = useState(false);

  const availableOptions = useMemo((): AvailableOptions => {
    const benefitPeriods = new Set<string>();
    const inflationOptions = new Map<string, number>();
    const paymentTypes = new Set<PaymentOption>();

    allCarrierQuotes.forEach(quote => {
      const period = getBenefitPeriodFromQuote(quote);
      if (period === 'lifetime' || period > 0) {
        benefitPeriods.add(benefitPeriodToString(period));
      }

      const inflationLabel = getInitialInflationLabel(quote);
      if (inflationLabel) {
        const rate = parseInflationRate(inflationLabel);
        inflationOptions.set(inflationLabel, rate);
      }

      paymentTypes.add(getPaymentOptionFromQuote(quote));
    });

    const sortedPeriods = Array.from(benefitPeriods)
      .map(stringToBenefitPeriod)
      .sort((firstPeriod, secondPeriod) => {
        if (firstPeriod === 'lifetime') return 1;
        if (secondPeriod === 'lifetime') return -1;
        return firstPeriod - secondPeriod;
      });

    return {
      benefitPeriods: sortedPeriods,
      inflationOptions: Array.from(inflationOptions.entries())
        .sort((firstOption, secondOption) => firstOption[1] - secondOption[1])
        .map(([label, rate]) => ({ label, rate })),
      paymentTypes: Array.from(paymentTypes).sort(),
    };
  }, [allCarrierQuotes]);

  const getPremium = (quote: Quote): number => {
    if ('primaryModalPremium' in quote && typeof quote.primaryModalPremium === 'number') {
      return quote.primaryModalPremium;
    }
    return quote.premiumAmount || 0;
  };

  const initialQuoteValues = useMemo(
    () => ({
      premium: getPremium(topQuote),
      benefitPeriod: getBenefitPeriodFromQuote(topQuote),
      inflationLabel: getInitialInflationLabel(topQuote),
      paymentType: getPaymentOptionFromQuote(topQuote),
    }),
    [topQuote],
  );

  const [selectedPremium, setSelectedPremium] = useState(initialQuoteValues.premium);
  const [selectedBenefitPeriod, setSelectedBenefitPeriod] = useState<BenefitPeriod>(
    initialQuoteValues.benefitPeriod,
  );
  const [selectedInflationOption, setSelectedInflationOption] = useState<string>(() => {
    const matchingInflationOption = availableOptions.inflationOptions.find(
      option => option.label === initialQuoteValues.inflationLabel,
    );
    return matchingInflationOption?.label || availableOptions.inflationOptions[0]?.label || 'None';
  });
  const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentOption>(
    initialQuoteValues.paymentType,
  );

  const displayQuote = useMemo(() => {
    if (!client) return topQuote;

    const matchingQuote = allCarrierQuotes.find(quote =>
      matchesQuoteFilters({
        quote,
        selectedBenefitPeriod,
        selectedInflationOption,
        selectedPaymentType,
      }),
    );

    if (!matchingQuote) return topQuote;

    const premiumAdjustedQuote = adjustQuoteForPremium(matchingQuote, selectedPremium);

    try {
      const enriched = createQuoteWithROIandProjectedCoverage(
        premiumAdjustedQuote,
        client,
        returnOnInvestmentCalculationType,
      );
      return {
        ...enriched,
        formattedROI: formatROI(enriched.roiValues, returnOnInvestmentCalculationType),
      };
    } catch {
      return premiumAdjustedQuote;
    }
  }, [
    client,
    allCarrierQuotes,
    topQuote,
    selectedBenefitPeriod,
    selectedInflationOption,
    selectedPaymentType,
    selectedPremium,
    returnOnInvestmentCalculationType,
  ]);

  const matchingQuotesCount = useMemo(() => {
    return allCarrierQuotes.filter(quote =>
      matchesQuoteFilters({
        quote,
        selectedBenefitPeriod,
        selectedInflationOption,
        selectedPaymentType,
      }),
    ).length;
  }, [allCarrierQuotes, selectedBenefitPeriod, selectedInflationOption, selectedPaymentType]);

  const hasFilters = useMemo(() => {
    return (
      Math.abs(selectedPremium - initialQuoteValues.premium) > 100 ||
      !benefitPeriodsMatch(selectedBenefitPeriod, initialQuoteValues.benefitPeriod) ||
      selectedInflationOption !== initialQuoteValues.inflationLabel ||
      selectedPaymentType !== initialQuoteValues.paymentType
    );
  }, [
    selectedPremium,
    selectedBenefitPeriod,
    selectedInflationOption,
    selectedPaymentType,
    initialQuoteValues,
  ]);

  const resetFilters = () => {
    setSelectedPremium(initialQuoteValues.premium);
    setSelectedBenefitPeriod(initialQuoteValues.benefitPeriod);
    setSelectedInflationOption(initialQuoteValues.inflationLabel);
    setSelectedPaymentType(initialQuoteValues.paymentType);
  };

  const quotesForPaymentType = useMemo(
    () =>
      allCarrierQuotes.filter(
        carrierQuote => getPaymentOptionFromQuote(carrierQuote) === selectedPaymentType,
      ),
    [allCarrierQuotes, selectedPaymentType],
  );

  const sliderBounds = useMemo(() => {
    return calculateInvestmentBounds(quotesForPaymentType, {
      min: topQuote.investmentMin || 10000,
      max: topQuote.investmentMax || 200000,
    });
  }, [quotesForPaymentType, topQuote]);

  useEffect(() => {
    if (quotesForPaymentType.length > 0) {
      const firstQuote = quotesForPaymentType[0];
      const min = sliderBounds.min;
      const max = sliderBounds.max;
      const clampedPremium = Math.max(min, Math.min(max, getPremium(firstQuote)));
      setSelectedPremium(clampedPremium);
    }
  }, [quotesForPaymentType, sliderBounds.min, sliderBounds.max]);

  return {
    isCustomizing,
    setIsCustomizing,
    displayQuote,
    selectedPremium,
    setSelectedPremium,
    selectedBenefitPeriod,
    setSelectedBenefitPeriod,
    selectedInflationOption,
    setSelectedInflationOption,
    selectedPaymentType,
    setSelectedPaymentType,
    availableOptions,
    hasFilters,
    resetFilters,
    totalMatchingQuotes: matchingQuotesCount,
    sliderBounds,
  };
}
